import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import LoadingState from "@/components/base/LoadingState";
import AdminLayout from "@/components/feature/AdminLayout";
import AdminHeader from "@/components/feature/AdminHeader";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";

/* ─── Types ──────────────────────────────────────────────────── */
type Tab = "general" | "square" | "stripe" | "offline";

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: "general", label: "General",         icon: "ri-settings-3-line" },
  { id: "square",  label: "Square",          icon: "ri-layout-grid-line" },
  { id: "stripe",  label: "Stripe",          icon: "ri-bank-card-line" },
  { id: "offline", label: "Offline Methods", icon: "ri-hand-coin-line" },
];

type IntRow = { id: string; integration_key: string; is_enabled: boolean; settings: Record<string, string> };

/* ─── Shared UI ──────────────────────────────────────────────── */
function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-slate-600 text-xs font-medium mb-1.5 block">{label}</label>
      {children}
      {hint && <p className="text-slate-400 text-[11px] mt-1">{hint}</p>}
    </div>
  );
}

function TInput({
  name, value, onChange, placeholder, type = "text", mono, disabled,
}: {
  name: string; value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string; type?: string; mono?: boolean; disabled?: boolean;
}) {
  return (
    <input
      name={name} type={type} value={value} onChange={onChange}
      placeholder={placeholder} disabled={disabled}
      className={`w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#C8A96E]/70 focus:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${mono ? "font-mono" : ""}`}
    />
  );
}

function Toggle({
  enabled, onChange, disabled, color = "#C8A96E",
}: {
  enabled: boolean; onChange: (v: boolean) => void; disabled?: boolean; color?: string;
}) {
  return (
    <button
      type="button" disabled={disabled}
      onClick={() => !disabled && onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 shrink-0 ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
      style={{ backgroundColor: enabled ? color : "#e2e8f0" }}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${enabled ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

function ModeSelector<T extends string>({
  value, options, onChange,
}: {
  value: T;
  options: { value: T; label: string; color: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex items-center gap-1 bg-slate-50 rounded-lg p-1 border border-slate-200">
      {options.map((opt) => (
        <button
          key={opt.value} type="button"
          onClick={() => onChange(opt.value)}
          className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
            value === opt.value ? "text-white" : "text-slate-400 hover:text-slate-600"
          }`}
          style={value === opt.value ? { backgroundColor: opt.color } : {}}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function SaveBar({
  saving, saved, error, onSave,
}: {
  saving: boolean; saved: boolean; error: string | null; onSave: () => void;
}) {
  return (
    <div className="flex items-center justify-between pt-5 border-t border-slate-100 mt-6">
      <div className="text-xs">
        {error && <span className="flex items-center gap-1.5 text-red-500"><i className="ri-error-warning-line" /> {error}</span>}
        {saved && !error && <span className="flex items-center gap-1.5 text-emerald-600"><i className="ri-check-double-line" /> Saved successfully</span>}
        {!saved && !error && <span className="text-slate-400">Saved in tenant_integrations per key</span>}
      </div>
      <button
        type="button" onClick={onSave} disabled={saving}
        className="flex items-center gap-2 px-6 py-2.5 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
      >
        {saving
          ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</>
          : <><i className="ri-save-3-line" />Save Changes</>}
      </button>
    </div>
  );
}

function InfoBanner({ icon, color, children }: { icon: string; color: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 p-3.5 rounded-xl border" style={{ backgroundColor: `${color}10`, borderColor: `${color}25` }}>
      <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
        <i className={`${icon} text-sm`} style={{ color }} />
      </span>
      <p className="text-xs leading-relaxed" style={{ color }}>
        {children}
      </p>
    </div>
  );
}

function ReadinessItem({ label, done }: { label: string; done: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className={`w-4 h-4 flex items-center justify-center rounded-full shrink-0 ${done ? "bg-emerald-100" : "bg-slate-100"}`}>
        <i className={`text-[10px] ${done ? "ri-check-line text-emerald-500" : "ri-time-line text-slate-300"}`} />
      </div>
      <span className={`text-xs ${done ? "text-slate-600" : "text-slate-400"}`}>{label}</span>
    </div>
  );
}

/* ─── Page ──────────────────────────────────────────────────── */
export default function AdminPayments() {
  const navigate = useNavigate();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("general");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [rows, setRows] = useState<Record<string, IntRow>>({});

  /* ── Form states ── */
  const [generalForm, setGeneralForm] = useState({
    currency: "USD",
    tax_rate: "0",
    card_fee_percent: "3.5",
    auto_apply_card_fee: false,
  });

  const [squareForm, setSquareForm] = useState({
    enabled: false,
    environment: "sandbox" as "sandbox" | "production",
    location_id: "",
    application_id: "",
    token_last4: "",
  });

  const [stripeForm, setStripeForm] = useState({
    enabled: false,
    mode: "sandbox" as "sandbox" | "live",
    publishable_key_last4: "",
    secret_key_configured: false,
    webhook_configured: false,
    currency: "usd",
  });

  const [offlineForm, setOfflineForm] = useState({
    cash_enabled: false,
    zelle_enabled: false,
    zelle_info: "",
    venmo_enabled: false,
    venmo_info: "",
    bank_transfer_enabled: false,
    bank_info: "",
    check_enabled: false,
    check_payable_to: "",
  });

  /* ─── Fetch ── */
  const fetchData = useCallback(async (tid: string) => {
    const { data } = await supabase
      .from("tenant_integrations")
      .select("id, integration_key, is_enabled, settings")
      .eq("tenant_id", tid)
      .in("integration_key", ["payments_general", "square", "stripe", "offline_methods"]);

    const map: Record<string, IntRow> = {};
    for (const r of (data as IntRow[]) ?? []) map[r.integration_key] = r;
    setRows(map);

    const gen = map["payments_general"]?.settings ?? {};
    if (gen) {
      setGeneralForm({
        currency:             gen.currency           ?? "USD",
        tax_rate:             gen.tax_rate           ?? "0",
        card_fee_percent:     gen.card_fee_percent   ?? "3.5",
        auto_apply_card_fee:  gen.auto_apply_card_fee === "true",
      });
    }

    const sq = map["square"];
    if (sq) {
      setSquareForm({
        enabled:        sq.is_enabled ?? false,
        environment:    (sq.settings?.environment as "sandbox" | "production") ?? "sandbox",
        location_id:    sq.settings?.location_id    ?? "",
        application_id: sq.settings?.application_id ?? "",
        token_last4:    sq.settings?.token_last4    ?? "",
      });
    }

    const st = map["stripe"];
    if (st) {
      setStripeForm({
        enabled:                st.is_enabled ?? false,
        mode:                   (st.settings?.mode as "sandbox" | "live") ?? "sandbox",
        publishable_key_last4:  st.settings?.publishable_key_last4  ?? "",
        secret_key_configured:  st.settings?.secret_key_configured  === "true",
        webhook_configured:     st.settings?.webhook_configured      === "true",
        currency:               st.settings?.currency               ?? "usd",
      });
    }

    const off = map["offline_methods"]?.settings ?? {};
    if (off) {
      setOfflineForm({
        cash_enabled:          off.cash_enabled          === "true",
        zelle_enabled:         off.zelle_enabled         === "true",
        zelle_info:            off.zelle_info            ?? "",
        venmo_enabled:         off.venmo_enabled         === "true",
        venmo_info:            off.venmo_info            ?? "",
        bank_transfer_enabled: off.bank_transfer_enabled === "true",
        bank_info:             off.bank_info             ?? "",
        check_enabled:         off.check_enabled         === "true",
        check_payable_to:      off.check_payable_to      ?? "",
      });
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchData(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchData]);

  /* ─── Upsert helper ── */
  const upsert = async (key: string, isEnabled: boolean, settings: Record<string, string>) => {
    if (!tenantId) return;
    const existing = rows[key];
    if (existing) {
      await supabase.from("tenant_integrations")
        .update({ is_enabled: isEnabled, settings, updated_at: new Date().toISOString() })
        .eq("id", existing.id);
    } else {
      await supabase.from("tenant_integrations").insert({
        tenant_id: tenantId, integration_key: key, is_enabled: isEnabled, settings,
      });
    }
  };

  /* ─── Save ── */
  const handleSave = async () => {
    if (!tenantId) return;
    setSaving(true); setSaved(false); setSaveError(null);
    try {
      if (activeTab === "general") {
        await upsert("payments_general", true, {
          currency:           generalForm.currency,
          tax_rate:           generalForm.tax_rate,
          card_fee_percent:   generalForm.card_fee_percent,
          auto_apply_card_fee: generalForm.auto_apply_card_fee ? "true" : "false",
        });
      } else if (activeTab === "square") {
        await upsert("square", squareForm.enabled, {
          environment:    squareForm.environment,
          location_id:    squareForm.location_id,
          application_id: squareForm.application_id,
          token_last4:    squareForm.token_last4,
        });
      } else if (activeTab === "stripe") {
        await upsert("stripe", stripeForm.enabled, {
          mode:                  stripeForm.mode,
          publishable_key_last4: stripeForm.publishable_key_last4,
          secret_key_configured: stripeForm.secret_key_configured ? "true" : "false",
          webhook_configured:    stripeForm.webhook_configured    ? "true" : "false",
          currency:              stripeForm.currency,
        });
      } else if (activeTab === "offline") {
        await upsert("offline_methods", true, {
          cash_enabled:          offlineForm.cash_enabled          ? "true" : "false",
          zelle_enabled:         offlineForm.zelle_enabled         ? "true" : "false",
          zelle_info:            offlineForm.zelle_info,
          venmo_enabled:         offlineForm.venmo_enabled         ? "true" : "false",
          venmo_info:            offlineForm.venmo_info,
          bank_transfer_enabled: offlineForm.bank_transfer_enabled ? "true" : "false",
          bank_info:             offlineForm.bank_info,
          check_enabled:         offlineForm.check_enabled         ? "true" : "false",
          check_payable_to:      offlineForm.check_payable_to,
        });
      }
      await fetchData(tenantId);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  /* ─── CURRENCIES ── */
  const CURRENCIES = [
    { value: "USD", label: "USD — US Dollar" },
    { value: "EUR", label: "EUR — Euro" },
    { value: "GBP", label: "GBP — British Pound" },
    { value: "MXN", label: "MXN — Mexican Peso" },
    { value: "BRL", label: "BRL — Brazilian Real" },
    { value: "CAD", label: "CAD — Canadian Dollar" },
    { value: "AUD", label: "AUD — Australian Dollar" },
    { value: "COP", label: "COP — Colombian Peso" },
    { value: "ARS", label: "ARS — Argentine Peso" },
    { value: "CLP", label: "CLP — Chilean Peso" },
    { value: "PEN", label: "PEN — Peruvian Sol" },
  ];

  /* ─── Tab: General ── */
  function GeneralTab() {
    const fee = parseFloat(generalForm.card_fee_percent) || 0;
    const tax = parseFloat(generalForm.tax_rate) || 0;
    const example = 1000;
    const feeAmount = (example * fee / 100).toFixed(2);
    const taxAmount = (example * tax / 100).toFixed(2);
    const total = (example + (generalForm.auto_apply_card_fee ? example * fee / 100 : 0) + example * tax / 100).toFixed(2);

    return (
      <div className="space-y-5">
        <Field label="Default Currency" hint="Used for contracts, invoices and payment display">
          <select
            value={generalForm.currency}
            onChange={(e) => setGeneralForm((p) => ({ ...p, currency: e.target.value }))}
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/70 cursor-pointer appearance-none"
          >
            {CURRENCIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </Field>

        <Field label="Tax Rate (%)" hint="Applied to invoice totals. Set 0 to disable.">
          <div className="flex items-center gap-2">
            <TInput
              name="tax_rate"
              type="number"
              value={generalForm.tax_rate}
              onChange={(e) => setGeneralForm((p) => ({ ...p, tax_rate: e.target.value }))}
              placeholder="0"
            />
            <span className="text-slate-400 text-sm font-medium shrink-0">%</span>
          </div>
        </Field>

        <Field label="Card Processing Fee (%)" hint="Fee passed to client when paying by card">
          <div className="flex items-center gap-2">
            <TInput
              name="card_fee_percent"
              type="number"
              value={generalForm.card_fee_percent}
              onChange={(e) => setGeneralForm((p) => ({ ...p, card_fee_percent: e.target.value }))}
              placeholder="3.5"
            />
            <span className="text-slate-400 text-sm font-medium shrink-0">%</span>
          </div>
        </Field>

        <div className="flex items-start justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
          <div>
            <p className="text-slate-700 text-sm font-semibold">Auto-apply card fee</p>
            <p className="text-slate-400 text-xs mt-0.5">
              Automatically add the card fee to payment totals when client pays by card
            </p>
          </div>
          <Toggle
            enabled={generalForm.auto_apply_card_fee}
            onChange={(v) => setGeneralForm((p) => ({ ...p, auto_apply_card_fee: v }))}
          />
        </div>

        {/* Live calc preview */}
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
          <p className="text-slate-600 text-xs font-semibold mb-3 flex items-center gap-2">
            <i className="ri-calculator-line text-slate-400" />
            Live Fee Preview — on a {generalForm.currency} {example.toLocaleString()} invoice
          </p>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-slate-500 text-xs">Base amount</span>
              <span className="text-slate-700 text-xs font-mono">{generalForm.currency} {example.toLocaleString()}.00</span>
            </div>
            {tax > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-slate-500 text-xs">Tax ({generalForm.tax_rate}%)</span>
                <span className="text-slate-700 text-xs font-mono">+ {generalForm.currency} {taxAmount}</span>
              </div>
            )}
            {generalForm.auto_apply_card_fee && fee > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-slate-500 text-xs">Card fee ({generalForm.card_fee_percent}%)</span>
                <span className="text-slate-700 text-xs font-mono">+ {generalForm.currency} {feeAmount}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-slate-200">
              <span className="text-slate-700 text-xs font-semibold">Client pays</span>
              <span className="text-[#0f172a] text-sm font-bold font-mono">{generalForm.currency} {total}</span>
            </div>
          </div>
        </div>

        <InfoBanner icon="ri-information-line" color="#C8A96E">
          These are global defaults. Individual contracts can override currency and fee settings per contract.
        </InfoBanner>
      </div>
    );
  }

  /* ─── Tab: Square ── */
  function SquareTab() {
    return (
      <div className="space-y-5">
        {/* Enable */}
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0"
              style={{ backgroundColor: squareForm.enabled ? "#F9731620" : "#f1f5f9" }}>
              <i className="ri-layout-grid-line text-lg" style={{ color: squareForm.enabled ? "#F97316" : "#94a3b8" }} />
            </div>
            <div>
              <p className="text-slate-700 text-sm font-semibold">Enable Square Payments</p>
              <p className="text-slate-400 text-xs mt-0.5">In-person POS and online card payments</p>
            </div>
          </div>
          <Toggle enabled={squareForm.enabled} onChange={(v) => setSquareForm((p) => ({ ...p, enabled: v }))} color="#F97316" />
        </div>

        {/* Environment */}
        <Field label="Environment">
          <div className="mt-1">
            <ModeSelector
              value={squareForm.environment}
              options={[
                { value: "sandbox",    label: "Sandbox",    color: "#F97316" },
                { value: "production", label: "Production", color: "#22C55E" },
              ]}
              onChange={(v) => setSquareForm((p) => ({ ...p, environment: v }))}
            />
          </div>
          <p className="text-slate-400 text-[11px] mt-1.5">
            {squareForm.environment === "sandbox"
              ? "Test mode — use Square test accounts. No real charges."
              : "Production — real cards will be charged."}
          </p>
        </Field>

        {squareForm.environment === "production" && (
          <div className="flex items-start gap-2.5 p-3.5 rounded-xl border border-amber-200 bg-amber-50">
            <i className="ri-error-warning-line text-amber-500 text-sm mt-0.5 shrink-0" />
            <p className="text-amber-700 text-xs leading-relaxed">
              Production mode active. Ensure your Square Access Token in Supabase Edge Function secrets
              is a <strong>production token</strong>, not a sandbox token.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Field label="Application ID" hint="From Square Developer Dashboard">
            <TInput
              name="application_id"
              value={squareForm.application_id}
              onChange={(e) => setSquareForm((p) => ({ ...p, application_id: e.target.value }))}
              placeholder="sandbox-sq0idb-..."
              mono
            />
          </Field>
          <Field label="Location ID" hint="Your Square business location ID">
            <TInput
              name="location_id"
              value={squareForm.location_id}
              onChange={(e) => setSquareForm((p) => ({ ...p, location_id: e.target.value }))}
              placeholder="LXXXXXXXXXXXXXXXXX"
              mono
            />
          </Field>
        </div>

        <Field
          label="Access Token — Last 4 Chars"
          hint="For reference only. Full token must be in Supabase Edge Function secrets."
        >
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm font-mono shrink-0">EAA…</span>
            <TInput
              name="token_last4"
              value={squareForm.token_last4}
              onChange={(e) => setSquareForm((p) => ({ ...p, token_last4: e.target.value.slice(0, 4) }))}
              placeholder="a3f9"
              mono
            />
          </div>
        </Field>

        <InfoBanner icon="ri-shield-keyhole-line" color="#F97316">
          <strong>Security:</strong> Store your Square Access Token as{" "}
          <span className="font-mono font-bold">SQUARE_ACCESS_TOKEN</span> in Supabase Edge Function secrets.
          Only the last 4 chars are stored here for reference.
        </InfoBanner>

        <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
          <p className="text-slate-600 text-xs font-semibold mb-3">Readiness checklist</p>
          <div className="space-y-2">
            <ReadinessItem label="Integration enabled"         done={squareForm.enabled} />
            <ReadinessItem label="Environment selected"        done={true} />
            <ReadinessItem label="Application ID set"          done={!!squareForm.application_id.trim()} />
            <ReadinessItem label="Location ID set"             done={!!squareForm.location_id.trim()} />
            <ReadinessItem label="Access token in Supabase secrets" done={!!squareForm.token_last4.trim()} />
          </div>
        </div>
      </div>
    );
  }

  /* ─── Tab: Stripe ── */
  function StripeTab() {
    return (
      <div className="space-y-5">
        {/* Enable */}
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0"
              style={{ backgroundColor: stripeForm.enabled ? "#C8A96E20" : "#f1f5f9" }}>
              <i className="ri-bank-card-line text-lg" style={{ color: stripeForm.enabled ? "#C8A96E" : "#94a3b8" }} />
            </div>
            <div>
              <p className="text-slate-700 text-sm font-semibold">Enable Stripe Payments</p>
              <p className="text-slate-400 text-xs mt-0.5">Card payments, payment links, PCI-compliant</p>
            </div>
          </div>
          <Toggle enabled={stripeForm.enabled} onChange={(v) => setStripeForm((p) => ({ ...p, enabled: v }))} />
        </div>

        {/* Mode */}
        <Field label="Mode">
          <div className="mt-1">
            <ModeSelector
              value={stripeForm.mode}
              options={[
                { value: "sandbox", label: "Test Mode", color: "#F97316" },
                { value: "live",    label: "Live Mode", color: "#22C55E" },
              ]}
              onChange={(v) => setStripeForm((p) => ({ ...p, mode: v }))}
            />
          </div>
          <p className="text-slate-400 text-[11px] mt-1.5">
            {stripeForm.mode === "sandbox"
              ? "Use test card 4242 4242 4242 4242. No real charges."
              : "Live mode — real cards will be charged. Use sk_live_... in secrets."}
          </p>
        </Field>

        {stripeForm.mode === "live" && (
          <div className="flex items-start gap-2.5 p-3.5 rounded-xl border border-amber-200 bg-amber-50">
            <i className="ri-error-warning-line text-amber-500 text-sm mt-0.5 shrink-0" />
            <p className="text-amber-700 text-xs leading-relaxed">
              Live mode active. Ensure your <span className="font-mono font-semibold">STRIPE_SECRET_KEY</span> in
              Supabase secrets is a <strong>live key</strong> (<span className="font-mono">sk_live_…</span>), not a test key.
            </p>
          </div>
        )}

        <Field label="Currency" hint="Default currency for Stripe payment sessions">
          <select
            value={stripeForm.currency}
            onChange={(e) => setStripeForm((p) => ({ ...p, currency: e.target.value }))}
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/70 cursor-pointer appearance-none"
          >
            {[
              { value: "usd", label: "USD — US Dollar" },
              { value: "eur", label: "EUR — Euro" },
              { value: "gbp", label: "GBP — British Pound" },
              { value: "mxn", label: "MXN — Mexican Peso" },
              { value: "brl", label: "BRL — Brazilian Real" },
              { value: "cad", label: "CAD — Canadian Dollar" },
              { value: "aud", label: "AUD — Australian Dollar" },
            ].map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </Field>

        <Field
          label="Publishable Key — Last 4 Chars"
          hint="pk_test_... or pk_live_... — last 4 chars only for reference"
        >
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm font-mono shrink-0">pk_…</span>
            <TInput
              name="publishable_key_last4"
              value={stripeForm.publishable_key_last4}
              onChange={(e) => setStripeForm((p) => ({ ...p, publishable_key_last4: e.target.value.slice(0, 4) }))}
              placeholder="4f2a"
              mono
            />
          </div>
        </Field>

        {/* Secret key checkbox */}
        <div
          onClick={() => setStripeForm((p) => ({ ...p, secret_key_configured: !p.secret_key_configured }))}
          className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all ${
            stripeForm.secret_key_configured
              ? "border-emerald-200 bg-emerald-50"
              : "border-slate-200 bg-slate-50 hover:border-slate-300"
          }`}
        >
          <div>
            <p className={`text-sm font-semibold ${stripeForm.secret_key_configured ? "text-emerald-700" : "text-slate-600"}`}>
              Secret key stored in Supabase secrets
            </p>
            <p className="text-slate-400 text-xs mt-0.5">
              Mark as done once <span className="font-mono">STRIPE_SECRET_KEY</span> is in Edge Function secrets
            </p>
          </div>
          <div className={`w-5 h-5 flex items-center justify-center rounded-full shrink-0 ${
            stripeForm.secret_key_configured ? "bg-emerald-500" : "border-2 border-slate-300"
          }`}>
            {stripeForm.secret_key_configured && <i className="ri-check-line text-white text-xs" />}
          </div>
        </div>

        {/* Webhook checkbox */}
        <div
          onClick={() => setStripeForm((p) => ({ ...p, webhook_configured: !p.webhook_configured }))}
          className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all ${
            stripeForm.webhook_configured
              ? "border-emerald-200 bg-emerald-50"
              : "border-slate-200 bg-slate-50 hover:border-slate-300"
          }`}
        >
          <div>
            <p className={`text-sm font-semibold ${stripeForm.webhook_configured ? "text-emerald-700" : "text-slate-600"}`}>
              Webhook configured
            </p>
            <p className="text-slate-400 text-xs mt-0.5">
              Endpoint: <span className="font-mono">/functions/v1/stripe-webhook</span>
            </p>
          </div>
          <div className={`w-5 h-5 flex items-center justify-center rounded-full shrink-0 ${
            stripeForm.webhook_configured ? "bg-emerald-500" : "border-2 border-slate-300"
          }`}>
            {stripeForm.webhook_configured && <i className="ri-check-line text-white text-xs" />}
          </div>
        </div>

        <InfoBanner icon="ri-shield-keyhole-line" color="#C8A96E">
          <strong>Security:</strong> Store <span className="font-mono font-bold">STRIPE_SECRET_KEY</span> and{" "}
          <span className="font-mono font-bold">STRIPE_WEBHOOK_SECRET</span> in Supabase Edge Function secrets only.
          Never enter full keys in this form.
        </InfoBanner>

        <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
          <p className="text-slate-600 text-xs font-semibold mb-3">Readiness checklist</p>
          <div className="space-y-2">
            <ReadinessItem label="Integration enabled"              done={stripeForm.enabled} />
            <ReadinessItem label="Mode selected"                    done={true} />
            <ReadinessItem label="Currency configured"              done={!!stripeForm.currency} />
            <ReadinessItem label="Publishable key reference set"    done={!!stripeForm.publishable_key_last4.trim()} />
            <ReadinessItem label="Secret key in Supabase secrets"   done={stripeForm.secret_key_configured} />
            <ReadinessItem label="Webhook configured"               done={stripeForm.webhook_configured} />
          </div>
        </div>
      </div>
    );
  }

  /* ─── Tab: Offline Methods ── */
  function OfflineTab() {
    const methods = [
      {
        key: "cash_enabled" as const,
        label: "Cash",
        icon: "ri-money-dollar-circle-line",
        color: "#22C55E",
        infoKey: null,
        infoLabel: null,
        infoPlaceholder: null,
      },
      {
        key: "zelle_enabled" as const,
        label: "Zelle",
        icon: "ri-exchange-dollar-line",
        color: "#6B48FF",
        infoKey: "zelle_info" as const,
        infoLabel: "Zelle Phone / Email",
        infoPlaceholder: "studio@gmail.com or +1 555 000 0000",
      },
      {
        key: "venmo_enabled" as const,
        label: "Venmo",
        icon: "ri-paypal-line",
        color: "#008CFF",
        infoKey: "venmo_info" as const,
        infoLabel: "Venmo Handle",
        infoPlaceholder: "@YourStudio",
      },
      {
        key: "bank_transfer_enabled" as const,
        label: "Bank Transfer / ACH / Wire",
        icon: "ri-bank-line",
        color: "#0f172a",
        infoKey: "bank_info" as const,
        infoLabel: "Bank Instructions",
        infoPlaceholder: "Bank name, account ending in …1234, routing …",
      },
      {
        key: "check_enabled" as const,
        label: "Check",
        icon: "ri-draft-line",
        color: "#94a3b8",
        infoKey: "check_payable_to" as const,
        infoLabel: "Payable To",
        infoPlaceholder: "Studio Legal Name or DBA",
      },
    ];

    const enabledCount = methods.filter((m) => offlineForm[m.key]).length;

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-1">
          <p className="text-slate-600 text-xs">
            Enable offline payment methods shown to clients in the portal
          </p>
          <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-[#C8A96E]/15 text-[#a6853e]">
            {enabledCount} active
          </span>
        </div>

        {methods.map((method) => {
          const isOn = offlineForm[method.key];
          return (
            <div
              key={method.key}
              className={`rounded-xl border transition-all ${
                isOn ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50"
              }`}
            >
              {/* Method header */}
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0"
                    style={{ backgroundColor: isOn ? `${method.color}18` : "#f1f5f9" }}
                  >
                    <i className={`${method.icon} text-base`} style={{ color: isOn ? method.color : "#94a3b8" }} />
                  </div>
                  <p className={`text-sm font-semibold ${isOn ? "text-[#0f172a]" : "text-slate-400"}`}>
                    {method.label}
                  </p>
                </div>
                <Toggle
                  enabled={isOn}
                  onChange={(v) => setOfflineForm((p) => ({ ...p, [method.key]: v }))}
                  color={method.color}
                />
              </div>

              {/* Info field when enabled */}
              {isOn && method.infoKey && (
                <div className="px-4 pb-4">
                  <label className="text-slate-500 text-xs font-medium mb-1.5 block">
                    {method.infoLabel}
                  </label>
                  <input
                    value={offlineForm[method.infoKey]}
                    onChange={(e) => setOfflineForm((p) => ({ ...p, [method.infoKey!]: e.target.value }))}
                    placeholder={method.infoPlaceholder ?? ""}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#C8A96E]/70 focus:bg-white transition-colors"
                  />
                  <p className="text-slate-400 text-[11px] mt-1">
                    Shown to clients in the payment section of the portal
                  </p>
                </div>
              )}
            </div>
          );
        })}

        <InfoBanner icon="ri-information-line" color="#C8A96E">
          Offline methods are shown as options in the client portal payment section. Payments made
          offline must be recorded manually in the Payments module.
        </InfoBanner>
      </div>
    );
  }

  const tabContent: Record<Tab, React.ReactNode> = {
    general: <GeneralTab />,
    square:  <SquareTab />,
    stripe:  <StripeTab />,
    offline: <OfflineTab />,
  };

  /* ── Tab status dots ── */
  const tabStatus: Record<Tab, boolean> = {
    general: !!(rows["payments_general"]),
    square:  rows["square"]?.is_enabled ?? false,
    stripe:  rows["stripe"]?.is_enabled ?? false,
    offline: !!(rows["offline_methods"]?.settings && Object.values(rows["offline_methods"].settings).some((v) => v === "true")),
  };

  /* ─── Render ── */
  return (
    <AdminLayout>
      <AdminHeader
        title="Payment Settings"
        subtitle="Configure currency, fees and payment gateway connections"
      />
      <div className="px-4 md:px-8 py-6">
        {loading ? (
          <LoadingState />
        ) : (
          <div className="max-w-4xl">
            {/* Tabs */}
            <div className="flex items-center gap-1 bg-white rounded-xl border border-slate-200 p-1.5 mb-6">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => { setActiveTab(tab.id); setSaved(false); setSaveError(null); }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === tab.id ? "bg-[#0f172a] text-white" : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <i className={`${tab.icon} text-sm`} />
                  {tab.label}
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${tabStatus[tab.id] ? "bg-emerald-400" : "bg-slate-300"}`} />
                </button>
              ))}
            </div>

            {/* Content card */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              {/* Tab header */}
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-slate-100">
                <div className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-lg">
                  <i className={`${TABS.find((t) => t.id === activeTab)?.icon} text-slate-600`} />
                </div>
                <div>
                  <p className="text-[#0f172a] font-semibold text-sm">
                    {TABS.find((t) => t.id === activeTab)?.label}
                  </p>
                  <p className="text-slate-400 text-[11px]">
                    {activeTab === "general"  && "Global currency, tax and card fee defaults"}
                    {activeTab === "square"   && "Square payment gateway configuration"}
                    {activeTab === "stripe"   && "Stripe payment gateway configuration"}
                    {activeTab === "offline"  && "Manual payment methods shown to clients"}
                  </p>
                </div>
                <span className="ml-auto text-slate-400 text-[11px] font-mono bg-slate-50 px-2.5 py-1 rounded-md">
                  tenant_integrations
                </span>
              </div>

              {tabContent[activeTab]}

              <SaveBar saving={saving} saved={saved} error={saveError} onSave={handleSave} />
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}